<template>
  <div class="chart-card">
    <div class="chart-card-header">
      <Dropdown
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="chart in availableCharts"
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>

      <Dropdown
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="sort in availableSorts"
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>

      <Dropdown
        :titleDropdown="selectedUf || 'UF'"
        position="right"
        colorArrow="black"
      >
        <ul style="max-height: 200px; overflow-y: auto;">
          <li
            v-for="uf in availableUfs"
            :key="uf.code || 'all'"
            @click="changeUf(uf.code)"
            class="bkps-dropdown-item"
            :title="uf.name"
          >
            {{ uf.code || 'Todas' }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 400px; width: 100%"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'
import { states } from '@/constants/states'

export default {
  components: { Dropdown },
  props: {
    graphData: { required: true, type: Array },
    showDetails: { type: Boolean, default: false }
  },
  data() {
    return {
      currentType: 'donut',
      sortBy: 'value',
      chartInstance: null,
      selectedUf: '',
      availableUfs: [{ code: '', name: 'Todas as UFs' }, ...states],
      availableCharts: [
        {
          type: 'pie',
          name: 'Pizza',
          icon: 'fas fa-chart-pie',
          description: 'Gráfico de pizza semi-circular'
        },
        {
          type: 'donut',
          name: 'Donut',
          icon: 'fas fa-dot-circle',
          description: 'Gráfico donut moderno'
        },
        {
          type: 'bar',
          name: 'Barras',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras'
        }
      ],
      availableSorts: [
        {
          type: 'value',
          name: 'Valor',
          icon: 'fas fa-dollar-sign',
          description: 'Exibir por valor (R$)'
        },
        {
          type: 'count',
          name: 'Qtd',
          icon: 'fas fa-hashtag',
          description: 'Exibir por quantidade'
        }
      ]
    }
  },
  computed: {
    selectedChart() {
      return this.availableCharts.find(chart => chart.type === this.currentType)
    },
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeChartType(type) {
      this.currentType = type
      this.createChart()
    },
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    changeUf(code) {
      this.selectedUf = code
      this.$emit('uf-change', code)
    },
    buildDisplayData() {
      const normalizedData = this.graphData.map(item => {
        if (Array.isArray(item)) {
          return { name: item[0], sum: item[1], rawValue: item[1] / 100, count: 0 }
        }
        return {
          name: item.name,
          sum: item.y || 0,
          rawValue: typeof item.y === 'number' ? item.y / 100 : 0,
          count: item.count || 0
        }
      })

      const metricField = this.sortBy === 'count' ? 'count' : 'sum'
      const total = normalizedData.reduce((acc, item) => acc + (item[metricField] || 0), 0)

      return normalizedData.map(item => ({
        ...item,
        y: total ? (item[metricField] * 100) / total : 0,
        customPercent: total ? (item[metricField] * 100) / total : 0
      }))
    },
    createChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const theme = this.chartTheme
      const sortBy = this.sortBy
      const displayData = this.buildDisplayData()
      let chartConfig = {}

      const colorConnectors = function (chart) {
        const series = chart.series && chart.series[0]
        if (!series) return
        series.points.forEach(function (point) {
          if (point.connector) {
            point.connector.attr({ stroke: point.color })
          }
        })
      }

      if (this.currentType === 'pie') {
        chartConfig = {
          chart: {
            type: 'pie',
            backgroundColor: theme.backgroundColor,
            height: 400,
            style: { fontFamily: '"Montserrat", sans-serif' },
            events: { render: function () { colorConnectors(this) } }
          },
          plotOptions: {
            pie: {
              borderRadius: 8,
              borderWidth: 2,
              borderColor: theme.backgroundColor,
              dataLabels: {
                enabled: true,
                distance: 10,
                connectorWidth: 1,
                connectorPadding: 3,
                style: {
                  fontWeight: '600',
                  fontSize: '11px',
                  color: theme.textColor,
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              }
            }
          },
          series: [{
            name: 'Dados',
            colorByPoint: true,
            data: displayData
          }]
        }
      } else if (this.currentType === 'donut') {
        chartConfig = {
          chart: {
            type: 'pie',
            backgroundColor: theme.backgroundColor,
            height: 400,
            marginTop: 60,
            marginBottom: 60,
            style: { fontFamily: '"Montserrat", sans-serif' },
            events: { render: function () { colorConnectors(this) } }
          },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              borderRadius: 8,
              borderWidth: 3,
              borderColor: theme.backgroundColor,
              center: ['50%', '50%'],
              size: '85%',
              dataLabels: {
                enabled: true,
                distance: 15,
                connectorWidth: 1,
                connectorPadding: 5,
                style: {
                  fontWeight: '600',
                  fontSize: '11px',
                  color: theme.textColor,
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              },
              innerSize: '60%'
            }
          },
          series: [{
            name: 'Dados',
            colorByPoint: true,
            data: displayData
          }]
        }
      } else if (this.currentType === 'bar') {
        const sortedDisplayData = [...displayData].sort((a, b) => (b.y || 0) - (a.y || 0))
        const categories = sortedDisplayData.map(item => item.name)
        const barData = sortedDisplayData.map(item => ({
          name: item.name,
          y: item.y,
          rawValue: item.rawValue,
          count: item.count
        }))

        chartConfig = {
          chart: {
            type: 'column',
            backgroundColor: theme.backgroundColor,
            height: 400,
            marginBottom: 160,
            marginTop: 60,
            marginLeft: 70,
            marginRight: 20,
            style: { fontFamily: '"Montserrat", sans-serif' }
          },
          xAxis: {
            categories: categories,
            labels: {
              rotation: -45,
              overflow: 'allow',
              style: { color: theme.textColor, fontSize: '10px' }
            }
          },
          yAxis: {
            title: {
              text: sortBy === 'value' ? 'Porcentagem por Valor (%)' : 'Porcentagem por Quantidade (%)',
              style: { color: theme.textColor }
            },
            labels: { style: { color: theme.textColor } },
            gridLineColor: theme.gridLineColor
          },
          legend: { enabled: false },
          plotOptions: {
            column: {
              borderRadius: 6,
              borderWidth: 0
            }
          },
          series: [{
            type: 'column',
            name: 'Dados',
            data: barData,
            colorByPoint: true
          }]
        }
      }

      const baseConfig = {
        colors: theme.colors,
        credits: { enabled: false },
        title: {
          text: `${this.currentTitle}`,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        tooltip: {
          outside: true,
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          borderRadius: 8,
          style: { color: theme.textColor, fontSize: '12px', zIndex: 9999 },
          headerFormat: '',
          formatter: function() {
            const lines = [
              `<span style="color:${this.point.color}">${this.point.name}</span>`,
              `Porcentagem: <b>${Highcharts.numberFormat(this.y, 2, ',', '.')}%</b>`
            ]
            if (typeof this.point.rawValue === 'number') {
              lines.push(`Valor: <b>R$ ${Highcharts.numberFormat(this.point.rawValue, 2, ',', '.')}</b>`)
            }
            if (typeof this.point.count === 'number') {
              lines.push(`Quantidade: <b>${Highcharts.numberFormat(this.point.count, 0, ',', '.')}</b>`)
            }
            return lines.join('<br/>')
          }
        }
      }

      this.chartInstance = Highcharts.chart(this.$refs.targetChart, { ...baseConfig, ...chartConfig })

      this.$nextTick(() => {
        if (this.chartInstance) this.chartInstance.reflow()
      })
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    graphData: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  }
}
</script>
